"use client";

import { create } from "zustand";
import { toast } from "@/hooks/use-toast";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  presidential?: boolean;
}

interface CartStore {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addItem: (item: Omit<CartItem, "quantity">) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  total: () => number;
  count: () => number;
}

export const useCart = create<CartStore>((set, get) => ({
  items: [],
  isOpen: false,
  openCart: () => set({ isOpen: true }),
  closeCart: () => set({ isOpen: false }),
  addItem: (item) => {
    const existing = get().items.find((i) => i.id === item.id);
    if (existing) {
      set({
        items: get().items.map((i) =>
          i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
        ),
      });
    } else {
      set({ items: [...get().items, { ...item, quantity: 1 }] });
    }
    toast({
      title: item.presidential ? "👑 Ajouté au coffret présidentiel" : "✓ Ajouté au panier",
      description: item.name,
    });
  },
  removeItem: (id) => set({ items: get().items.filter((i) => i.id !== id) }),
  clearCart: () => set({ items: [] }),
  total: () => get().items.reduce((sum, i) => sum + i.price * i.quantity, 0),
  count: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
}));

// ═══════════ UI STORE : dialogs & navigation ═══════════
interface UIStore {
  /** Monde ouvert dans la boutique : id du monde ou "tout" */
  boutiqueMonde: string | null;
  openBoutique: (monde: string) => void;
  closeBoutique: () => void;
  /** Merveille ouverte (id) */
  merveilleId: string | null;
  openMerveille: (id: string) => void;
  closeMerveille: () => void;
  forfaitsOpen: boolean;
  openForfaits: () => void;
  closeForfaits: () => void;
  checkoutOpen: boolean;
  openCheckout: () => void;
  closeCheckout: () => void;
  devisOpen: boolean;
  openDevis: () => void;
  closeDevis: () => void;
}

export const useUI = create<UIStore>((set) => ({
  boutiqueMonde: null,
  openBoutique: (monde) => set({ boutiqueMonde: monde }),
  closeBoutique: () => set({ boutiqueMonde: null }),
  merveilleId: null,
  openMerveille: (id) => set({ merveilleId: id }),
  closeMerveille: () => set({ merveilleId: null }),
  forfaitsOpen: false,
  openForfaits: () => set({ forfaitsOpen: true }),
  closeForfaits: () => set({ forfaitsOpen: false }),
  checkoutOpen: false,
  openCheckout: () => set({ checkoutOpen: true }),
  closeCheckout: () => set({ checkoutOpen: false }),
  devisOpen: false,
  openDevis: () => set({ devisOpen: true }),
  closeDevis: () => set({ devisOpen: false }),
}));
